import api from "@/http/xior";
import { User, Child, Assessment, Questionnaire, Question, Response } from "@/types/dashboard.types";

type AdminStats = {
  totalUsers: number;
  totalParents: number;
  totalAdmins: number;
  totalChildren: number;
  totalAssessments: number;
  assessmentsToday: number;
  assessmentsThisWeek: number;
  assessmentsThisMonth: number;
  childStatus: {
    active: number;
    inactive: number;
    withAssessments: number;
    withoutAssessments: number;
  };
  assessmentStatus: {
    inProgress: number;
    completed: number;
    analyzed: number;
  };
  riskDistribution: {
    low: number;
    mid: number;
    high: number;
  };
  riskPercentages: {
    low: string;
    mid: string;
    high: string;
  };
  activeQuestionnaire: (Questionnaire & { _count?: { questions: number } }) | null;
};

type AdminUsersPayload = {
  users: Array<{
    id: string;
    name: string;
    email: string;
    role: "parent" | "admin";
    status: "active" | "inactive";
    children: number;
    lastLogin: string;
    joinedDate: string;
  }>;
  stats: {
    total: number;
    active: number;
    parents: number;
    admins: number;
  };
};

export type AdminCreateUserPayload = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  role: "PARENT" | "ADMIN";
  isActive?: boolean;
  child?: {
    firstName: string;
    lastName?: string;
    dateOfBirth: string;
    gender: "MALE" | "FEMALE" | "OTHER";
  };
};

export type AdminUpdateUserPayload = {
  firstName?: string;
  lastName?: string;
  email?: string;
  isActive?: boolean;
};

export type AdminQuestionnaireListItem = Questionnaire & {
  _count?: {
    questions: number;
  };
};

/**
 * Auth API Services
 */
export const AuthAPI = {
  login: async (credentials: any) => {
    const res = await api.post('/auth/login', credentials);
    return res.data.data;
  },
  register: async (userData: any) => {
    const res = await api.post('/auth/register', userData);
    return res.data.data;
  },
  me: async () => {
    const res = await api.post('/auth/sessionToken');
    return res.data.data;
  }
};

/**
 * Child API Services
 */
export const ChildAPI = {
  getChildren: async () => {
    const res = await api.get<{ data: Child[] }>('/children');
    return res.data.data; // ApiResponse format
  },
  getChild: async (id: string) => {
    const res = await api.get<{ data: Child }>(`/children/${id}`);
    return res.data.data;
  },
  createChild: async (data: Partial<Child>) => {
    const res = await api.post<{ data: Child }>('/children', data);
    return res.data.data;
  },
  updateChild: async (id: string, data: Partial<Child>) => {
    const res = await api.put<{ data: Child }>(`/children/${id}`, data);
    return res.data.data;
  },
  deleteChild: async (id: string) => {
    const res = await api.delete(`/children/${id}`);
    return res.data;
  },
  getAssessmentHistory: async (id: string) => {
    const res = await api.get<{ data: Assessment[] }>(`/children/${id}/history`);
    return res.data.data;
  }
};

/**
 * Questionnaire API Services
 */
export const QuestionnaireAPI = {
  getActiveQuestionnaire: async (childId?: string) => {
    const res = await api.get<{ data: Questionnaire & { questionsByDomain: any; summary?: Questionnaire['summary'] } }>('/questionnaires/active', {
      params: childId ? { childId } : undefined,
    });
    return res.data.data;
  },
  getQuestionnaires: async () => {
    const res = await api.get<{ data: Questionnaire[] }>('/questionnaires');
    return res.data.data;
  },
  getAdminQuestionnaires: async () => {
    const res = await api.get<{ data: AdminQuestionnaireListItem[] }>('/questionnaires/admin/list');
    return res.data.data;
  },
  getQuestionnaire: async (id: string) => {
    const res = await api.get<{ data: Questionnaire & { questions?: Question[] } }>(`/questionnaires/${id}`);
    return res.data.data;
  },
  getQuestionnaireQuestions: async (id: string) => {
    const res = await api.get<{ data: any }>(`/questionnaires/${id}/questions`);
    return res.data.data;
  },
  activateQuestionnaire: async (id: string) => {
    const res = await api.patch<{ data: AdminQuestionnaireListItem }>(`/questionnaires/${id}/activate`);
    return res.data.data;
  },
  createQuestion: async (questionnaireId: string, payload: any) => {
    const res = await api.post<{ data: any }>(`/questionnaires/${questionnaireId}/questions`, payload);
    return res.data.data;
  },
  updateQuestion: async (questionId: string, payload: any) => {
    const res = await api.put<{ data: any }>(`/questionnaires/questions/${questionId}`, payload);
    return res.data.data;
  },
  deleteQuestion: async (questionId: string) => {
    const res = await api.delete<{ data: any }>(`/questionnaires/questions/${questionId}`);
    return res.data.data;
  }
};

/**
 * Assessment API Services
 */
export const AssessmentAPI = {
  startAssessment: async (childId: string) => {
    const res = await api.post<{ data: any }>('/assessments', { childId });
    return res.data.data;
  },
  saveResponse: async (assessmentId: string, questionId: string, answer: string) => {
    const res = await api.put<{ data: any }>(`/assessments/${assessmentId}/response`, { questionId, answer });
    return res.data.data;
  },
  submitAssessment: async (assessmentId: string, options?: { isPartial?: boolean }) => {
    const res = await api.post<{ data: any }>(`/assessments/${assessmentId}/submit`, {
      isPartial: options?.isPartial === true,
    });
    return res.data.data;
  },
  getAssessment: async (assessmentId: string) => {
    const res = await api.get<{ data: Assessment }>(`/assessments/${assessmentId}`);
    return res.data.data;
  },
  getAssessmentResult: async (assessmentId: string) => {
    const res = await api.get<{ data: any }>(`/assessments/${assessmentId}/result`);
    return res.data.data;
  },
  getAssessments: async (params?: { status?: string, childId?: string }) => {
    const res = await api.get<{ data: any }>('/assessments', { params });
    return res.data.data;
  }
};

/**
 * Admin API Services
 */
export const AdminAPI = {
  getStats: async () => {
    const res = await api.get<{ data: AdminStats }>('/admin/stats');
    return res.data.data;
  },
  getActivityLogs: async () => {
    const res = await api.get<{ data: any[] }>('/admin/audit-logs');
    return res.data.data;
  },
  getUsers: async () => {
    const res = await api.get<{ data: AdminUsersPayload }>('/admin/users');
    return res.data.data;
  },
  getModelInputTraces: async (params?: { assessmentId?: string; childId?: string; limit?: number }) => {
    const res = await api.get<{ data: any }>('/admin/model-input-traces', { params });
    return res.data.data;
  },
  createUser: async (payload: AdminCreateUserPayload) => {
    const res = await api.post<{ data: { user: User; child?: Child | null } }>('/admin/users', payload);
    return res.data.data;
  },
  updateUser: async (id: string, payload: AdminUpdateUserPayload) => {
    const res = await api.patch<{ data: any }>(`/admin/users/${id}`, payload);
    return res.data.data;
  },
  deleteUser: async (id: string) => {
    const res = await api.delete<{ data: any }>(`/admin/users/${id}`);
    return res.data.data;
  }
};
